<template>
  <div class="suppliers-view">
    <div class="page-header">
      <div class="header-left">
        <div class="header-info">
          <PageTitle title="Fournisseurs" description="Gérez vos partenaires et fournisseurs" />
        </div>
      </div>
      <div class="actions">
        <button class="primary-btn" @click="onAddSupplier">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Nouveau fournisseur
        </button>
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon total">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Total</span>
          <span class="stat-value">{{ pagination.total }}</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Actifs</span>
          <span class="stat-value">{{ activeCount }}</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon inactive">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </div>
        <div class="stat-content">
          <span class="stat-label">Inactifs</span>
          <span class="stat-value">{{ inactiveCount }}</span>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <div class="header-left">
          <div class="search-box">
            <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input type="text" v-model="filters.search" placeholder="Rechercher par nom, email, téléphone..."
              @input="debouncedFetch" />
            <button v-if="filters.search" @click="clearSearch" class="clear-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        <div class="header-actions">
          <button class="icon-btn" @click="fetchSuppliers" title="Actualiser">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Chargement des fournisseurs...</p>
      </div>

      <div v-else>
        <div v-if="suppliers.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <h3>Aucun fournisseur trouvé</h3>
          <p v-if="filters.search">Essayez de modifier vos critères de recherche</p>
          <p v-else>Commencez par ajouter votre premier fournisseur</p>
          <button v-if="!filters.search" class="create-first-btn" @click="onAddSupplier">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ajouter le premier fournisseur
          </button>
        </div>

        <div v-else>
          <div class="table-container">
            <table class="suppliers-table">
              <thead>
                <tr>
                  <th class="col-name">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Nom
                    </div>
                  </th>
                  <th class="col-contact">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      Contact
                    </div>
                  </th>
                  <th class="col-phone">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                        </path>
                      </svg>
                      Téléphone
                    </div>
                  </th>
                  <th class="col-status">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                      Statut
                    </div>
                  </th>
                  <th class="col-date">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      Créé le
                    </div>
                  </th>
                  <th class="col-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="supplier in suppliers" :key="supplier.id" class="supplier-row"
                  @click="goToDetails(supplier.id)">
                  <td class="col-name">
                    <div class="supplier-cell">
                      <div class="supplier-avatar">
                        {{ getInitials(supplier.name) }}
                      </div>
                      <div class="supplier-info">
                        <span class="supplier-name">{{ supplier.name }}</span>
                        <span v-if="supplier.contact_person" class="supplier-contact">{{ supplier.contact_person
                          }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="col-contact">
                    <div v-if="supplier.email" class="contact-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <span>{{ supplier.email }}</span>
                    </div>
                    <span v-else class="no-data">-</span>
                  </td>
                  <td class="col-phone">
                    <div v-if="supplier.phone" class="phone-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                        </path>
                      </svg>
                      <span>{{ supplier.phone }}</span>
                    </div>
                    <span v-else class="no-data">-</span>
                  </td>
                  <td class="col-status">
                    <span class="status-badge" :class="supplier.active ? 'status-active' : 'status-inactive'">
                      <span class="status-dot"></span>
                      {{ supplier.active ? 'Actif' : 'Inactif' }}
                    </span>
                  </td>
                  <td class="col-date">
                    <div class="date-cell">
                      {{ formatDate(supplier.created_at) }}
                    </div>
                  </td>
                  <td class="col-action">
                    <button class="action-btn" @click.stop="goToDetails(supplier.id)">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination-wrapper">
            <PaginationComponent :currentPage="pagination.page" :perPage="Number(pagination.per_page)"
              :total="pagination.total"
              :to="pagination.to || Math.min(pagination.page * Number(pagination.per_page), pagination.total)"
              :showPageNumbers="true" :maxVisiblePages="5" @page-change="goToPage" />
          </div>
        </div>
      </div>
    </div>

    <CreateSupplierModal :show="showCreateModal" @close="showCreateModal = false" @created="onSupplierCreated" />
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import PageTitle from '@/components/common/PageTitle.vue'
import supplierService from '@/services/supplierService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import CreateSupplierModal from './CreateSupplierModal.vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'SuppliersView',
  components: { PageTitle, CreateSupplierModal, PaginationComponent },
  setup() {
    const loading = ref(false)
    const suppliers = ref([])
    const router = useRouter()
    const showCreateModal = ref(false)
    const filters = ref({
      search: '',
      per_page: 10
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })
    let debounceTimer = null

    const activeCount = computed(() => {
      return suppliers.value.filter(s => s.active).length
    })

    const inactiveCount = computed(() => {
      return suppliers.value.filter(s => !s.active).length
    })

    const fetchSuppliers = async () => {
      try {
        loading.value = true
        const res = await supplierService.getSuppliers({
          search: filters.value.search || undefined,
          per_page: filters.value.per_page,
          page: pagination.value.page,
          sort_by: 'name',
          sort_direction: 'asc'
        })
        const data = res.data
        if (Array.isArray(data)) {
          suppliers.value = data
          pagination.value.total = data.length
          pagination.value.per_page = filters.value.per_page
          pagination.value.from = data.length ? 1 : 0
          pagination.value.to = data.length
        } else {
          suppliers.value = data.data || data.items || []
          pagination.value.total = Number(data.total || (data.meta && data.meta.total) || suppliers.value.length) || 0
          pagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || filters.value.per_page) || 10
          pagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || pagination.value.page) || 1
          pagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((pagination.value.page - 1) * pagination.value.per_page + 1)) || 0
          pagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)) || 0
        }
      } catch (e) {
        console.error('Erreur chargement fournisseurs:', e)
        suppliers.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        pagination.value.page = 1
        fetchSuppliers()
      }, 300)
    }

    const clearSearch = () => {
      filters.value.search = ''
      pagination.value.page = 1
      fetchSuppliers()
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchSuppliers()
    }

    const onAddSupplier = () => {
      showCreateModal.value = true
    }

    const onSupplierCreated = () => {
      showCreateModal.value = false
      pagination.value.page = 1
      fetchSuppliers()
    }

    const goToDetails = (id) => {
      router.push({ name: 'supplier-detail', params: { id } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    fetchSuppliers()

    return {
      loading,
      suppliers,
      filters,
      pagination,
      activeCount,
      inactiveCount,
      fetchSuppliers,
      debouncedFetch,
      clearSearch,
      goToPage,
      onAddSupplier,
      onSupplierCreated,
      showCreateModal,
      formatDate,
      goToDetails,
      getInitials
    }
  }
})
</script>

<style scoped>
.suppliers-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  flex: 1;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
  transition: all 0.2s;
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.primary-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
  stroke-width: 2;
}

.stat-icon.total {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.stat-icon.total svg {
  stroke: #0284c7;
}

.stat-icon.active {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.stat-icon.active svg {
  stroke: #2e7d32;
}

.stat-icon.inactive {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
}

.stat-icon.inactive svg {
  stroke: #616161;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.5px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #f1f5f9;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  gap: 16px;
}

.search-box {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 14px;
  min-width: 360px;
  transition: all 0.2s;
}

.search-box:focus-within {
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.search-icon {
  width: 18px;
  height: 18px;
  stroke: #94a3b8;
  stroke-width: 2;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 12px;
  font-size: 14px;
  color: #0f172a;
  background: transparent;
}

.clear-btn {
  background: #f1f5f9;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.clear-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.icon-btn svg {
  width: 18px;
  height: 18px;
  stroke: #475569;
  stroke-width: 2;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 400px;
}

.create-first-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.create-first-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.create-first-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.table-container {
  overflow-x: auto;
}

.suppliers-table {
  width: 100%;
  border-collapse: collapse;
}

.suppliers-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
}

.suppliers-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.th-content svg {
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.suppliers-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.supplier-row {
  cursor: pointer;
  transition: all 0.2s;
}

.supplier-row:hover {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.supplier-row:last-child td {
  border-bottom: none;
}

.col-name {
  min-width: 250px;
}

.col-contact {
  min-width: 220px;
}

.col-phone {
  min-width: 160px;
}

.col-status {
  min-width: 120px;
}

.col-date {
  min-width: 140px;
}

.col-action {
  width: 60px;
  text-align: center;
}

.supplier-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.supplier-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.supplier-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.supplier-name {
  font-weight: 700;
  color: #0f172a;
}

.supplier-contact {
  font-size: 12px;
  color: #64748b;
}

.contact-info,
.phone-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
}

.contact-info svg,
.phone-info svg {
  width: 14px;
  height: 14px;
  stroke: #94a3b8;
  stroke-width: 2;
  flex-shrink: 0;
}

.no-data {
  color: #cbd5e1;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-badge.status-active {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.status-badge.status-active .status-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.status-badge.status-inactive {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  color: #616161;
}

.status-badge.status-inactive .status-dot {
  background: #616161;
  box-shadow: 0 0 0 3px rgba(97, 97, 97, 0.2);
}

.date-cell {
  color: #64748b;
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  border-color: #0A5C6B;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke: #475569;
  stroke-width: 2;
  transition: stroke 0.2s;
}

.action-btn:hover svg {
  stroke: white;
}

.pagination-wrapper {
  padding: 20px 24px;
  border-top: 2px solid #f1f5f9;
  background: #fafbfc;
}

@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .suppliers-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .actions {
    width: 100%;
  }

  .primary-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
    width: 100%;
  }

  .table-container {
    overflow-x: auto;
    margin: 0 -24px;
  }

  .suppliers-table {
    min-width: 800px;
  }
}
</style>